/* ================================================================
   Render Tree - Maps RenderNode[] to Three.js-compatible props.
   This module is purely data→presentation, no business logic.
   ================================================================ */

import type { RenderNode, SideId } from '../model/types';
import { DEFAULT_MATERIAL_LIBRARY } from '../materials';

// --------------- Material colors ---------------

const DEFAULT_COLOR = '#c8a96e';
const SELECTED_COLOR = '#4fc3f7';
const HOVER_COLOR = '#81d4fa';

const DEFAULT_COLORS_BY_KEY = new Map(
  DEFAULT_MATERIAL_LIBRARY.map(m => [m.key, m.color]),
);

export function getBaseColor(node: RenderNode): string {
  if (node.materialcolor && node.materialcolor !== '') {
    return node.materialcolor;
  }
  return DEFAULT_COLORS_BY_KEY.get(node.material) ?? DEFAULT_COLOR;
}

export function getSelectedColor(): string {
  return SELECTED_COLOR;
}

export function getHoverColor(): string {
  return HOVER_COLOR;
}

// --------------- Side display labels ---------------

const SIDE_LABELS: Record<SideId, string> = {
  pedestal: '底座',
  sideFront: '前面',
  endLeft: '左端',
  endRight: '右端',
  sideBack: '后面',
  topCap: '顶盖',
};

export function getSideLabel(sideId: SideId): string {
  return SIDE_LABELS[sideId] ?? sideId;
}

// --------------- Mesh transform helpers ---------------

/**
 * Convert mm dimensions to scene units (1 unit = 1mm, scaled down for display).
 * We use a scale factor so a 1200mm crate doesn't fill the entire viewport.
 */
export const SCALE_FACTOR = 0.001; // 1mm = 0.001 scene units

export function toScenePosition(x: number, y: number, z: number): [number, number, number] {
  return [x * SCALE_FACTOR, y * SCALE_FACTOR, z * SCALE_FACTOR];
}

export function toSceneSize(w: number, h: number, d: number): [number, number, number] {
  return [w * SCALE_FACTOR, h * SCALE_FACTOR, d * SCALE_FACTOR];
}

/**
 * Calculate the center offset so the crate is centered at origin.
 */
export function calculateCenterOffset(
  outerLength: number,
  outerWidth: number,
  outerHeight: number,
): [number, number, number] {
  // World: X=length, Y=height, Z=width
  return [
    -(outerLength / 2) * SCALE_FACTOR,
    -(outerHeight / 2) * SCALE_FACTOR,
    -(outerWidth / 2) * SCALE_FACTOR,
  ];
}

// --------------- Element type labels ---------------

export function getElementTypeLabel(type: string): string {
  switch (type) {
    case 'board': return '板';
    case 'frame': return '框';
    case 'horBrace': return '横撑';
    case 'bevelBrace': return '斜撑';
    default: return type;
  }
}
